"use client";
import React, { useState, useRef, useEffect } from "react";
import { Printer, Mail, Download } from "lucide-react";
import Button from "../ui/Button";
import { useRouter } from "next/dist/client/components/navigation";

const ShareIcon = () => (
  <svg
    className="shrink-0 text-[#FC7F16] group-hover:text-white transition-colors"
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.59375 1.08813C9.23438 1.24749 9 1.60685 9 2.00058V4.00049H5.5C2.4625 4.00049 0 6.46288 0 9.50025C0 13.0407 2.54688 14.6219 3.13125 14.9406C3.20937 14.9844 3.29688 15 3.38438 15C3.725 15 4 14.7219 4 14.3844C4 14.15 3.86562 13.9344 3.69375 13.7751C3.4 13.4969 3 12.9501 3 12.0001C3 10.344 4.34375 9.00027 6 9.00027H9V11.0002C9 11.3939 9.23125 11.7533 9.59375 11.9126C9.95625 12.072 10.375 12.0064 10.6687 11.7439L15.6687 7.2441C15.8781 7.05348 16 6.78474 16 6.50038C16 6.21602 15.8812 5.94728 15.6687 5.75667L10.6687 1.25687C10.375 0.991255 9.95312 0.925633 9.59375 1.08813Z"
      fill="currentColor"
    />
  </svg>
);


interface ShareDropdownProps {
    formationTitle?: string;
    programmeUrl?: string;
    /** When true, renders as a compact icon-only button (for tight spaces) */
    compact?: boolean;
    /** Extra classes applied to the outermost wrapper div */
    className?: string;
}

const ShareDropdown: React.FC<ShareDropdownProps> = ({
    formationTitle = "Formation",
    programmeUrl = "/assets/programme.pdf",
    compact = false,
    className = "",
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const router = useRouter();

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [open]);

    const handlePrint = () => {
        const currentUrl = window.location.pathname
        router.push(`${currentUrl}/programme`);
    };

    const handleEmail = () => {
        setOpen(false);
        const subject = encodeURIComponent(formationTitle);
        const body = encodeURIComponent(
            `Découvrez cette formation : ${typeof window !== "undefined" ? window.location.href : ""}`
        );
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    };

    const handleDownload = () => {
        setOpen(false);
        const link = document.createElement("a");
        link.href = programmeUrl;
        link.download = "programme-formation.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const actions = [
        {
            label: "Imprimer la page",
            icon: Printer,
            onClick: handlePrint,
        },
        {
            label: "Envoyer par e-mail",
            icon: Mail,
            onClick: handleEmail,
        },
        {
            label: "Télécharger le programme",
            icon: Download,
            onClick: handleDownload,
        },
    ];

    return (
        <div ref={containerRef} className={`relative ${className ?? ""}`}>
            <Button
                variant="outline-gradient"
                size="unset"
                className={`rounded-lg ${
                    compact ? "size-9 px-0" : "h-9 w-45.25 px-6"
                }`}
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={compact ? "Partager" : undefined}
                title="Partager"
            >
                <ShareIcon />
                {!compact && <span>Partager</span>}
            </Button>

            {open && (
                <div
                    role="listbox"
                    className="absolute top-full mt-2 right-0 z-50 bg-white border border-[#EFEFEF] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.12)] min-w-55 py-1.5 overflow-hidden"
                >
                    {actions.map(({ label, icon: Icon, onClick }) => (
                        <button
                            key={label}
                            type="button"
                            role="option"
                            aria-selected={false}
                            onClick={onClick}
                            className="w-full flex items-center gap-3 px-4 min-h-11 py-2.5 text-sm text-[#1B365E] hover:bg-[#F5FBFD] active:bg-[#C7F8FF] transition-colors text-left cursor-pointer"
                        >
                            <Icon size={16} className="shrink-0 text-[#40BFDE]" aria-hidden="true" />
                            <span className="font-medium">{label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ShareDropdown;
